import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { stockService } from '../../services/api';
import { Intrant, MouvementStock, PaginatedResponse } from '@/types';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  Package,
  AlertTriangle,
  TrendingDown,
  BarChart2,
  Plus,
  Search,
  X,
  ChevronRight,
  Clock,
  FileText,
  User,
  Layers,
  RefreshCw,
  Pencil,
} from 'lucide-react';
import IntrantForm from './IntrantForm';
import MouvementForm from './MouvementForm';

// ── Extended type to capture created_by_name from serializer ─────────────────
interface MouvementStockExtended extends MouvementStock {
  created_by_name?: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────
type MouvementTab = 'tous' | 'entree' | 'sortie';

const MOTIF_META: Record<string, { label: string; bg: string; text: string }> = {
  achat:        { label: 'Achat',        bg: 'bg-blue-50',   text: 'text-blue-700'   },
  livraison:    { label: 'Livraison',    bg: 'bg-indigo-50', text: 'text-indigo-700' },
  consommation: { label: 'Consommation', bg: 'bg-amber-50',  text: 'text-amber-700'  },
  perte:        { label: 'Perte',        bg: 'bg-red-50',    text: 'text-red-700'    },
  peremption:   { label: 'Péremption',   bg: 'bg-orange-50', text: 'text-orange-700' },
  ajustement:   { label: 'Ajustement',   bg: 'bg-purple-50', text: 'text-purple-700' },
};

const CATEGORY_PALETTE = [
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-pink-100 text-pink-700',
  'bg-teal-100 text-teal-700',
  'bg-orange-100 text-orange-700',
  'bg-cyan-100 text-cyan-700',
];

// ── Sub-components ────────────────────────────────────────────────────────────

function CategoryBadge({ id, nom }: { id: number; nom: string }) {
  const cls = CATEGORY_PALETTE[id % CATEGORY_PALETTE.length];
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${cls}`}>
      {nom}
    </span>
  );
}

function StatusBadge({ intrant }: { intrant: Intrant }) {
  if (intrant.est_en_rupture) {
    return (
      <span className="son-chip son-chip-red">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
        Rupture
      </span>
    );
  }
  if (intrant.est_critique) {
    return (
      <span className="son-chip son-chip-amber">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Critique
      </span>
    );
  }
  return (
    <span className="son-chip son-chip-green">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Normal
    </span>
  );
}

function StockBar({ intrant }: { intrant: Intrant }) {
  const stock = intrant.stock_actuel ?? 0;
  const max = Math.max(intrant.seuil_commande * 2, stock, 1);
  const pct = Math.min((stock / max) * 100, 100);
  const barCls = intrant.est_en_rupture
    ? 'bg-red-500'
    : intrant.est_critique
    ? 'bg-amber-500'
    : 'bg-[color:var(--teal)]';
  // Libellé de l'unité en français (fallback sur la valeur brute)
  const uniteLabel = intrant.unite_display || intrant.unite;

  return (
    <div className="space-y-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className={`text-base font-bold tabular-nums ${
          intrant.est_en_rupture ? 'text-[#C0392B]' : intrant.est_critique ? 'text-[#B5781A]' : 'text-[color:var(--text)]'
        }`}>
          {stock}
        </span>
        <span className="text-xs text-[color:var(--muted)] font-medium">{uniteLabel}</span>
      </div>
      <div className="flex-1 bg-[color:var(--border-soft)] rounded-full h-1.5 overflow-hidden" role="presentation">
        <div
          className={`h-full rounded-full transition-all duration-700 ease-out ${barCls}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  tone: string;
}
function KpiCard({ label, value, icon, tone }: KpiCardProps) {
  return (
    <div className="son-kpi">
      <div className={`son-kpi-icon ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <p className="son-kpi-label">{label}</p>
        <p className="son-kpi-value truncate">{value}</p>
      </div>
    </div>
  );
}

// ── Mouvement Drawer ──────────────────────────────────────────────────────────
interface DrawerProps {
  intrant: Intrant;
  onClose: () => void;
  onNewMouvement: () => void;
}

function MouvementDrawer({ intrant, onClose, onNewMouvement }: DrawerProps) {
  const [tab, setTab] = useState<MouvementTab>('tous');

  const mouvementParams = {
    intrant: intrant.id,
    ...(tab !== 'tous' && { type_mouvement: tab }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['mouvements', intrant.id, tab],
    queryFn: () =>
      stockService.getMouvements(mouvementParams).then(r => r.data as PaginatedResponse<MouvementStockExtended>),
  });

  const mouvements = data?.results || [];

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const TABS: { key: MouvementTab; label: string }[] = [
    { key: 'tous',   label: 'Tous' },
    { key: 'entree', label: 'Entrées' },
    { key: 'sortie', label: 'Sorties' },
  ];

  return (
    <>
      {/* Backdrop — démarre sous le header de l'app (h-14 = top-14) */}
      <div
        className="fixed inset-x-0 bottom-0 top-14 bg-black/40 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Drawer panel — ancré sous le header pour éviter l'overlap avec sticky top z-30 */}
      <div
        className="fixed right-0 top-14 bottom-0 w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label={`Historique des mouvements — ${intrant.nom}`}
        style={{ animation: 'drawerIn 0.28s cubic-bezier(0.22,1,0.36,1) both' }}
      >
        <style>{`
          @keyframes drawerIn {
            from { transform: translateX(100%); }
            to   { transform: translateX(0); }
          }
        `}</style>

        {/* ── Drawer header (dark) ── */}
        <div className="text-white px-5 pt-5 pb-4 flex-shrink-0" style={{ background: 'var(--grad-sidebar)' }}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <Package className="h-4.5 w-4.5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-0.5">
                  Historique des mouvements
                </p>
                <h3 className="font-bold text-white text-base leading-tight truncate">
                  {intrant.nom}
                </h3>
                <p className="text-xs text-white/60 font-mono">{intrant.code}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex-shrink-0 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
              aria-label="Fermer l'historique"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </div>

          {/* Total disponible — mise en avant */}
          <div
            className={`rounded-xl px-4 py-3 mb-3 flex items-center justify-between ${
              intrant.est_en_rupture
                ? 'bg-red-500/20 border border-red-400/30'
                : intrant.est_critique
                ? 'bg-amber-500/20 border border-amber-400/30'
                : 'bg-[#14A37A]/20 border border-[#2ED3A6]/30'
            }`}
          >
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
                Total disponible
              </p>
              <p
                className={`text-4xl font-bold leading-none mt-0.5 ${
                  intrant.est_en_rupture
                    ? 'text-red-400'
                    : intrant.est_critique
                    ? 'text-amber-400'
                    : 'text-[#2ED3A6]'
                }`}
              >
                {intrant.stock_actuel}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold text-white/60">{intrant.unite_display || intrant.unite}</p>
              <p className="text-[10px] text-white/60 mt-1">
                {intrant.est_en_rupture ? 'Rupture de stock' : intrant.est_critique ? 'Niveau critique' : 'Stock suffisant'}
              </p>
            </div>
          </div>

          {/* Seuils */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <p className="text-xl font-bold leading-none text-amber-400">
                {intrant.seuil_critique}
              </p>
              <p className="text-[10px] text-white/60 mt-0.5 leading-tight">Seuil critique</p>
            </div>
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <p className="text-xl font-bold leading-none text-sky-400">
                {intrant.seuil_commande}
              </p>
              <p className="text-[10px] text-white/60 mt-0.5 leading-tight">Seuil commande</p>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={onNewMouvement}
            className="btn-primary w-full py-2.5 text-sm"
          >
            <Plus className="h-4 w-4" />
            Nouveau mouvement
          </button>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-[color:var(--border)] bg-white flex-shrink-0" role="tablist">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              role="tab"
              aria-selected={tab === key}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                tab === key
                  ? 'border-[color:var(--teal)] text-[color:var(--text)]'
                  : 'border-transparent text-[color:var(--muted)] hover:text-[color:var(--text)]'
              }`}
            >
              {key === 'entree' && (
                <ArrowUpCircle className={`h-3.5 w-3.5 ${tab === key ? 'text-emerald-600' : ''}`} />
              )}
              {key === 'sortie' && (
                <ArrowDownCircle className={`h-3.5 w-3.5 ${tab === key ? 'text-red-500' : ''}`} />
              )}
              {label}
              {key !== 'tous' && data && data.count > 0 && tab === key && (
                <span className="ml-1 son-chip son-chip-gray !px-1.5">
                  {data.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Movements list ── */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="p-4 space-y-3" aria-busy="true">
              <span className="sr-only">Chargement...</span>
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="son-skeleton h-14" />)}
            </div>
          ) : mouvements.length === 0 ? (
            <div className="son-empty">
              <div className="son-empty-icon"><RefreshCw className="h-6 w-6" aria-hidden /></div>
              <p className="font-semibold text-[color:var(--text)]">Aucun mouvement</p>
              <p className="text-xs">
                Les mouvements {tab !== 'tous' ? `(${tab === 'entree' ? 'entrées' : 'sorties'})` : ''} apparaîtront ici.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[color:var(--border-soft)]">
              {mouvements.map((m) => {
                const isEntree = m.type_mouvement === 'entree';
                const motif = MOTIF_META[m.motif] ?? {
                  label: m.motif,
                  bg: 'bg-gray-100',
                  text: 'text-gray-600',
                };
                return (
                  <li key={m.id} className="px-4 py-3.5 hover:bg-[color:var(--panel-2)] transition-colors">
                    <div className="flex items-start gap-3">
                      {/* direction icon */}
                      <div
                        className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isEntree ? 'bg-emerald-50' : 'bg-red-50'
                        }`}
                      >
                        {isEntree ? (
                          <ArrowUpCircle className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <ArrowDownCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* top row: motif + qty */}
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-[11px] font-semibold ${motif.bg} ${motif.text}`}
                          >
                            {motif.label}
                          </span>
                          <span
                            className={`text-base font-bold tabular-nums ${
                              isEntree ? 'text-emerald-600' : 'text-red-500'
                            }`}
                          >
                            {isEntree ? '+' : '−'}{m.quantite}
                          </span>
                        </div>

                        {/* date */}
                        <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--muted)]">
                          <Clock className="h-3 w-3 flex-shrink-0" />
                          <span>{fmtDate(m.date_mouvement)}</span>
                        </div>

                        {/* created by */}
                        {m.created_by_name && (
                          <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--muted)] mt-0.5">
                            <User className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{m.created_by_name}</span>
                          </div>
                        )}

                        {/* reference */}
                        {m.reference_document && (
                          <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--muted)] mt-0.5">
                            <FileText className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate font-mono">{m.reference_document}</span>
                          </div>
                        )}

                        {/* notes */}
                        {m.notes && (
                          <p className="text-[11px] text-gray-500 italic mt-1 truncate">
                            {m.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const StockList: React.FC = () => {
  const [searchTerm, setSearchTerm]           = useState('');
  const [categorieFilter, setCategorieFilter] = useState('');
  const [statusFilter, setStatusFilter]       = useState<'tous' | 'rupture' | 'critique' | 'normal'>('tous');
  const [showIntrantForm, setShowIntrantForm]   = useState(false);
  const [showMouvementForm, setShowMouvementForm] = useState(false);
  const [selectedIntrant, setSelectedIntrant]  = useState<Intrant | null>(null);
  const [drawerIntrant, setDrawerIntrant]       = useState<Intrant | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<PaginatedResponse<Intrant>>(
    ['intrants', { search: searchTerm, categorie: categorieFilter }],
    () =>
      stockService
        .getIntrants({
          search: searchTerm,
          ...(categorieFilter && { categorie: categorieFilter }),
        })
        .then(r => r.data),
    { keepPreviousData: true }
  );

  const { data: statistiques } = useQuery({
    queryKey: ['stock-statistiques'],
    queryFn: () => stockService.getStatistiques().then(r => r.data),
  });

  const { data: stockCritique } = useQuery<Intrant[]>({
    queryKey: ['stock-critique'],
    queryFn: () => stockService.getStockCritique().then(r => r.data),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories-intrants'],
    queryFn: () => stockService.getCategories().then(r => r.data),
  });

  const allIntrants   = data?.results ?? [];
  const critiques     = stockCritique ?? [];
  const ruptures      = critiques.filter(i => i.est_en_rupture);
  const sousCritiques = critiques.filter(i => i.est_critique && !i.est_en_rupture);

  // Filtre statut côté client (est_en_rupture / est_critique sont des propriétés calculées)
  const intrants = allIntrants.filter(i => {
    if (statusFilter === 'rupture')  return i.est_en_rupture;
    if (statusFilter === 'critique') return i.est_critique && !i.est_en_rupture;
    if (statusFilter === 'normal')   return !i.est_critique && !i.est_en_rupture;
    return true;
  });

  const invalidateStock = () => {
    queryClient.invalidateQueries({ queryKey: ['intrants'] });
    queryClient.invalidateQueries({ queryKey: ['stock-statistiques'] });
    queryClient.invalidateQueries({ queryKey: ['stock-critique'] });
    queryClient.invalidateQueries({ queryKey: ['mouvements'] });
  };

  const handleEdit = (intrant: Intrant) => {
    setDrawerIntrant(null);
    setSelectedIntrant(intrant);
    setShowIntrantForm(true);
  };

  const handleMouvement = (intrant: Intrant) => {
    setDrawerIntrant(null);
    setSelectedIntrant(intrant);
    setShowMouvementForm(true);
  };

  const handleCloseForm = () => {
    setShowIntrantForm(false);
    setShowMouvementForm(false);
    setSelectedIntrant(null);
  };

  const handleSuccess = () => {
    invalidateStock();
    handleCloseForm();
  };

  // ── Form views (full page swap, matching existing pattern) ──
  if (showIntrantForm) {
    return (
      <IntrantForm
        intrant={selectedIntrant || undefined}
        onClose={handleCloseForm}
        onSuccess={handleSuccess}
      />
    );
  }

  if (showMouvementForm && selectedIntrant) {
    return (
      <MouvementForm
        intrant={selectedIntrant}
        onClose={handleCloseForm}
        onSuccess={handleSuccess}
      />
    );
  }

  // ── Main dashboard ──
  return (
    <div className="min-h-full bg-gray-50/50">
      {/* Drawer overlay */}
      {drawerIntrant && (
        <MouvementDrawer
          intrant={drawerIntrant}
          onClose={() => setDrawerIntrant(null)}
          onNewMouvement={() => handleMouvement(drawerIntrant)}
        />
      )}

      <div className="space-y-5">

        {/* ── Header ── */}
        <div className="son-page-head">
          <div>
            <h2 className="son-h1">
              Gestion du Stock
            </h2>
            <p className="son-sub">
              Inventaire des intrants dentaires — mouvements en temps réel
            </p>
          </div>
          <button
            onClick={() => setShowIntrantForm(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Nouvel intrant
          </button>
        </div>

        {/* ── KPI Cards ── */}
        {statistiques && (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <KpiCard
              label="Total intrants"
              value={statistiques.total_intrants}
              icon={<Layers className="h-5 w-5" aria-hidden />}
              tone="bg-[color:var(--blue-soft)] text-[#1565B8]"
            />
            <KpiCard
              label="Stock critique"
              value={statistiques.stock_critique}
              icon={<TrendingDown className="h-5 w-5" aria-hidden />}
              tone="bg-[#FBEAD2] text-[#B5781A]"
            />
            <KpiCard
              label="En rupture"
              value={statistiques.en_rupture}
              icon={<AlertTriangle className="h-5 w-5" aria-hidden />}
              tone="bg-[#FCE4E1] text-[#C0392B]"
            />
            <KpiCard
              label="Valeur du stock"
              value={
                statistiques.valeur_stock
                  ? statistiques.valeur_stock.toLocaleString('fr-GN') + ' GNF'
                  : '— GNF'
              }
              icon={<BarChart2 className="h-5 w-5" aria-hidden />}
              tone="bg-[color:var(--teal-soft)] text-[#0E8466]"
            />
          </div>
        )}

        {/* ── Alert banners — toujours visibles ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Ruptures */}
          <div
            className={`rounded-2xl p-4 border transition-colors ${
              ruptures.length > 0
                ? 'bg-[#FDF1EF] border-[#F6CFC9]'
                : 'son-panel'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle
                className={`h-4 w-4 flex-shrink-0 ${
                  ruptures.length > 0 ? 'text-[#C0392B]' : 'text-[color:var(--teal)]'
                }`}
              />
              <h3
                className={`text-sm font-semibold ${
                  ruptures.length > 0 ? 'text-[#C0392B]' : 'text-[color:var(--text)]'
                }`}
              >
                Ruptures de stock ({ruptures.length})
              </h3>
            </div>
            {ruptures.length === 0 ? (
              <p className="text-xs text-[color:var(--muted)]">Aucune rupture de stock</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {ruptures.map(i => (
                  <button
                    key={i.id}
                    onClick={() => setDrawerIntrant(i)}
                    className="son-chip son-chip-red hover:brightness-95 transition"
                  >
                    {i.nom}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Critiques */}
          <div
            className={`rounded-2xl p-4 border transition-colors ${
              sousCritiques.length > 0
                ? 'bg-[#FEF7EC] border-[#F3DDB8]'
                : 'son-panel'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown
                className={`h-4 w-4 flex-shrink-0 ${
                  sousCritiques.length > 0 ? 'text-[#B5781A]' : 'text-[color:var(--teal)]'
                }`}
              />
              <h3
                className={`text-sm font-semibold ${
                  sousCritiques.length > 0 ? 'text-[#B5781A]' : 'text-[color:var(--text)]'
                }`}
              >
                Niveaux critiques ({sousCritiques.length})
              </h3>
            </div>
            {sousCritiques.length === 0 ? (
              <p className="text-xs text-[color:var(--muted)]">Tous les niveaux sont satisfaisants</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {sousCritiques.map(i => (
                  <button
                    key={i.id}
                    onClick={() => setDrawerIntrant(i)}
                    className="son-chip son-chip-amber hover:brightness-95 transition"
                  >
                    {i.nom} — {i.stock_actuel} {i.unite_display || i.unite}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="son-table-wrap">

          {/* Filter bar */}
          <div className="flex flex-col sm:flex-row gap-3 p-3 border-b border-[color:var(--border)]">
            <div className="son-search">
              <Search aria-hidden />
              <input
                type="search"
                aria-label="Rechercher un intrant"
                placeholder="Rechercher un intrant..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="form-input text-gray-700"
              />
            </div>
            <select
              value={categorieFilter}
              onChange={e => setCategorieFilter(e.target.value)}
              aria-label="Filtrer par catégorie"
              className="form-select text-gray-700 sm:w-52"
            >
              <option value="">Toutes les catégories</option>
              {categories?.results?.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.nom}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
              aria-label="Filtrer par statut"
              className="form-select text-gray-700 sm:w-44"
            >
              <option value="tous">Tous les statuts</option>
              <option value="rupture">Rupture</option>
              <option value="critique">Critique</option>
              <option value="normal">Normal</option>
            </select>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="p-4 space-y-3" aria-busy="true">
              <span className="sr-only">Chargement du stock...</span>
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="son-skeleton h-10" />)}
            </div>
          ) : intrants.length === 0 ? (
            <div className="son-empty">
              <div className="son-empty-icon"><Package className="h-6 w-6" aria-hidden /></div>
              <p className="font-semibold text-[color:var(--text)]">Aucun intrant trouvé</p>
              <p>
                Modifiez votre recherche ou ajoutez un nouvel intrant.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="son-table">
                <thead>
                  <tr>
                    <th>
                      Produit
                    </th>
                    <th>
                      Catégorie
                    </th>
                    <th className="w-60">
                      Quantité disponible
                    </th>
                    <th>
                      Seuils
                    </th>
                    <th>
                      Statut
                    </th>
                    <th className="!text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {intrants.map(intrant => (
                    <tr
                      key={intrant.id}
                      className={`group ${
                        intrant.est_en_rupture ? 'bg-[#FDF1EF]/60' : ''
                      }`}
                    >
                      {/* Product */}
                      <td>
                        <p className="font-semibold leading-none">
                          {intrant.nom}
                        </p>
                        <p className="text-[11px] font-mono text-[color:var(--muted)] mt-1">{intrant.code}</p>
                        {intrant.description && (
                          <p className="text-xs text-[color:var(--muted)] mt-0.5 truncate max-w-xs hidden sm:block">
                            {intrant.description}
                          </p>
                        )}
                      </td>

                      {/* Category */}
                      <td>
                        {intrant.categorie_details ? (
                          <CategoryBadge
                            id={intrant.categorie_details.id}
                            nom={intrant.categorie_details.nom}
                          />
                        ) : (
                          <span className="text-xs text-[color:var(--muted)]">—</span>
                        )}
                      </td>

                      {/* Stock bar */}
                      <td className="w-60">
                        <StockBar intrant={intrant} />
                      </td>

                      {/* Thresholds */}
                      <td>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-xs text-[color:var(--muted)] whitespace-nowrap">
                            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                            Critique :{' '}
                            <span className="font-semibold text-[color:var(--text)] tabular-nums">
                              {intrant.seuil_critique}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-[color:var(--muted)] whitespace-nowrap">
                            <span className="w-2 h-2 rounded-full bg-sky-400 flex-shrink-0" />
                            Commande :{' '}
                            <span className="font-semibold text-[color:var(--text)] tabular-nums">
                              {intrant.seuil_commande}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td>
                        <StatusBadge intrant={intrant} />
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleMouvement(intrant)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap bg-[color:var(--teal-soft)] text-[#0E8466] hover:bg-[#CDEDE2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
                            title="Enregistrer un mouvement"
                          >
                            <Package className="h-3.5 w-3.5" />
                            Mouvement
                          </button>
                          <button
                            onClick={() => setDrawerIntrant(intrant)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap bg-[color:var(--blue-soft)] text-[#1565B8] hover:bg-[#D3E6F8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-300"
                            title="Voir l'historique des mouvements"
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                            Historique
                          </button>
                          <button
                            onClick={() => handleEdit(intrant)}
                            className="son-icon-btn"
                            title="Modifier l'intrant"
                            aria-label="Modifier l'intrant"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer count */}
          {!isLoading && allIntrants.length > 0 && (
            <div className="son-pager">
              <p>
                {intrants.length} intrant{intrants.length > 1 ? 's' : ''} affiché{intrants.length > 1 ? 's' : ''}
                {statusFilter !== 'tous' && (
                  <span className="ml-1">
                    · filtre : <span className="font-semibold text-[color:var(--text)]">{statusFilter}</span>
                  </span>
                )}
              </p>
              <p>{data?.count ?? allIntrants.length} au total</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockList;
